import { useCallback, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectTriggerProps,
  SelectValue,
  SelectProps,
} from '@antimatterhq/ui/components/select';
import { Text } from '@antimatterhq/ui/components/typography';

export type DropdownProps = {
  options: { value: string; name: string }[] | string[];
  onChange?: React.FormEventHandler<HTMLSpanElement>;
  onBlur?: React.FocusEventHandler<HTMLSpanElement>;
  placeholder?: string;
  className?: string;
  emptyState?: string | React.ReactNode;
  'data-testid'?: string;
  maxHeight?: number;
} & Pick<SelectProps, 'value' | 'onValueChange' | 'name' | 'disabled'> &
  Pick<SelectTriggerProps, 'variant'>;

const FormatOption = ({ name }: { name?: string }) => {
  return <Text>{name}</Text>;
};

export default function GenericDropdown({
  value,
  onValueChange,
  onChange,
  onBlur,
  options: initialOptions,
  placeholder,
  name,
  className,
  emptyState,
  'data-testid': dataTestid,
  variant = 'default',
  maxHeight,
  ...props
}: DropdownProps) {
  const options = useMemo(() => {
    if (typeof initialOptions[0] === 'string') {
      return initialOptions.map((option) => ({
        value: option,
        name: option,
      })) as { value: string; name: string }[];
    }
    return initialOptions as { value: string; name: string }[];
  }, [initialOptions]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [value, options],
  );

  const handleValueChange = useCallback(
    (val: string) => {
      onValueChange?.(val);
      const syntheticEvent = {
        target: { value: val, name },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      const blurEvent = {
        target: { name },
      } as unknown as React.FocusEvent<HTMLSpanElement>;

      onChange?.(syntheticEvent);
      setTimeout(() => {
        onBlur?.(blurEvent);
      }, 1);
    },
    [onValueChange, onChange, name, onBlur],
  );

  return (
    <Select
      value={value}
      onValueChange={handleValueChange}
      name={name}
      {...props}
    >
      <SelectTrigger
        name={name}
        variant={variant}
        className={className}
        data-testid={dataTestid}
      >
        <SelectValue placeholder={placeholder}>
          {selectedOption && <FormatOption name={selectedOption.name} />}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            data-testid={`${dataTestid}-option-${option.value}`}
          >
            <FormatOption name={option.name} />
          </SelectItem>
        ))}
        {!options.length && emptyState && (
          <div className="p-4 text-center">
            <Text color="info" className="italic">
              {emptyState}
            </Text>
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
